// Save Service — orchestrates manual save of garden objects
// Syncs dirty objects from GardenObjectState to the API

import { api } from './client'
import { gardenObjectState } from '@/state/GardenObjectState'
import { zoneState } from '@/zones/ZoneState'
import { getGeoAnchor } from '@/geodata/geoAnchorState'
import { getParcel } from '@/geodata/parcelState'
import { debug } from '@/utils/debug'
import { t } from '@/i18n'
import type { Garden, GardenObject, GroundShape, Point, SchemeOutline } from '@/types/garden'

export type SaveStatus = 'idle' | 'saving' | 'success' | 'error'
export type SaveStatusCallback = (status: SaveStatus, message?: string) => void

/**
 * Convert internal center-based coordinates to API top-left convention.
 * Inverse of GardenObjectState.importFromGarden() which does top-left → center.
 */
function toApiCoords(obj: GardenObject): { x: number; y: number } {
  return {
    x: Math.round(obj.x - obj.width / 2),
    y: Math.round(obj.y - obj.height / 2),
  }
}

/**
 * Build SVG geometry string for ground shapes
 */
function buildGeometry(obj: { type: string; width: number; height: number; points?: Point[]; pathWaypoints?: Point[]; pathWidth?: number }): string | undefined {
  switch (obj.type) {
    case 'rectangle':
      return `<rect width='${Math.round(obj.width)}' height='${Math.round(obj.height)}' />`
    case 'ellipse':
      return `<ellipsis width='${Math.round(obj.width)}' height='${Math.round(obj.height)}' />`
    case 'polygon': {
      const shape = obj as GroundShape
      if (shape.points && shape.points.length >= 3) {
        const hw = shape.width / 2
        const hh = shape.height / 2
        // Convert center-relative → top-left-relative (inverse of importFromGarden)
        const pointsStr = shape.points
          .map(p => `${Math.round(p.x + hw)},${Math.round(p.y + hh)}`)
          .join(' ')
        return `<polygon points='${pointsStr}' />`
      }
      return undefined
    }
    case 'path': {
      const shape = obj as GroundShape
      if (shape.pathWaypoints && shape.pathWaypoints.length >= 2 && shape.points) {
        const hw = shape.width / 2
        const hh = shape.height / 2
        const waypointsStr = shape.pathWaypoints
          .map(p => `${Math.round(p.x + hw)},${Math.round(p.y + hh)}`)
          .join(' ')
        const outlineStr = shape.points
          .map(p => `${Math.round(p.x + hw)},${Math.round(p.y + hh)}`)
          .join(' ')
        return `<path waypoints='${waypointsStr}' width='${shape.pathWidth ?? 50}' outline='${outlineStr}' />`
      }
      return undefined
    }
    default:
      return undefined
  }
}

/**
 * Build SVG string from SchemeOutline (inverse of parseOutlineGeometry)
 */
function buildOutlineSvg(outline: SchemeOutline): string {
  const pointsStr = outline.points
    .map(p => `${Math.round(p.x)},${Math.round(p.y)}`)
    .join(' ')
  return `<polygon points='${pointsStr}' data-x='${Math.round(outline.x)}' data-y='${Math.round(outline.y)}' />`
}

class SaveServiceManager {
  private statusCallbacks: SaveStatusCallback[] = []
  private _status: SaveStatus = 'idle'
  private _zoneDirty: boolean = false
  private _zoneDirtyEnabled: boolean = true
  private _geodataDirty: boolean = false
  private _isNewGarden: boolean = false
  private _pendingBackgroundImage: {
    dataUri: string; x: number; y: number; scale: number; rotation?: number
  } | null = null
  private _backgroundDirty: boolean = false

  /** Callback invoked after deferred garden creation (to update URL, etc.) */
  onGardenCreated?: (gardenId: string) => void

  /** Callback to capture a preview image. Called after save; returns JPEG data URI or null. */
  onCapturePreview?: () => string | null

  constructor() {
    // Track zone state changes as dirty (suppressed during import)
    zoneState.onChange(() => {
      if (this._zoneDirtyEnabled) this._zoneDirty = true
    })
  }

  get status(): SaveStatus {
    return this._status
  }

  /**
   * Save all dirty garden objects, zone/pin state, and geodata.
   * If gardenId is empty, creates the garden on the server first (deferred creation).
   */
  async save(gardenId: string, schemeOutline?: SchemeOutline, garden?: Garden): Promise<boolean> {
    if (this._status === 'saving') {
      debug.warn('SaveService', 'Save already in progress')
      return false
    }

    const dirtyObjects = gardenObjectState.getDirtyObjects()

    this.setStatus('saving')

    try {
      // Deferred creation: if garden has no server ID yet, create it now
      if (!gardenId && garden) {
        this.setStatus('saving', t('status.creatingGarden'))
        debug.log('SaveService', 'No garden ID — creating garden on server...')
        const result = await api.createGarden({
          name: garden.name || t('ui.defaultGardenName'),
          description: garden.description || undefined,
        })
        gardenId = result.id
        garden.id = result.id
        debug.success('SaveService', `Garden created on server: ${gardenId}`)

        this._isNewGarden = false

        // Notify caller (bootstrap/topBar) so it can update the URL
        this.onGardenCreated?.(gardenId)
      }

      // Process each dirty object
      if (dirtyObjects.length > 0) {
        debug.log('SaveService', `Saving ${dirtyObjects.length} dirty objects...`)
        for (const dirty of dirtyObjects) {
          switch (dirty.status) {
            case 'new': {
              // Create on server
              const obj = dirty.object
              const coords = toApiCoords(obj)
              const geometry = buildGeometry(obj as GroundShape)
              const result = await api.createGardenObject({
                gardenId,
                productId: obj.productId || '',
                x: coords.x,
                y: coords.y,
                rotation: obj.rotation,
                elevation: obj.elevation,
                zIndex: obj.zIndex,
                geometry,
                label: obj.label,
              })
              // Map local ID to server ID
              gardenObjectState.setServerId(obj.id, result.id)
              debug.log('SaveService', `Created: ${obj.id} → server ${result.id}`)
              break
            }
            case 'modified': {
              // Update on server
              const obj = dirty.object
              const serverId = dirty.serverId
              if (!serverId) {
                debug.warn('SaveService', `No server ID for modified object: ${obj.id}`)
                continue
              }
              const coords = toApiCoords(obj)
              const geometry = buildGeometry(obj as GroundShape)
              await api.updateGardenObject(serverId, {
                x: coords.x,
                y: coords.y,
                rotation: obj.rotation,
                elevation: obj.elevation,
                zIndex: obj.zIndex,
                geometry,
                label: obj.label,
              })
              debug.log('SaveService', `Updated: ${obj.id} (server ${serverId})`)
              break
            }
            case 'deleted': {
              // Delete on server
              const serverId = dirty.serverId
              if (!serverId) {
                debug.warn('SaveService', `No server ID for deleted object: ${dirty.object.id}`)
                continue
              }
              await api.deleteGardenObject(serverId)
              debug.log('SaveService', `Deleted: server ${serverId}`)
              break
            }
          }
        }
      }

      // Save zone/pin state + geodata in garden properties (always, even without dirty objects)
      try {
        const properties: Record<string, unknown> = {
          zones: zoneState.exportState(),
          lockedObjects: gardenObjectState.getObjects()
            .filter(o => o.locked)
            .map(o => gardenObjectState.getServerId(o.id))
            .filter(Boolean),
        }

        // Include geodata if geo-anchor is set
        const anchor = getGeoAnchor()
        if (anchor) {
          properties.geoAnchor = anchor
          properties.parcel = getParcel()
        }

        // Build garden-level attributes (outline-geometry + rotation)
        const gardenAttrs: Record<string, unknown> = {}
        if (garden) {
          gardenAttrs['rotation'] = garden.rotation ?? 0
        }
        if (schemeOutline && schemeOutline.points.length >= 3) {
          gardenAttrs['outline-geometry'] = buildOutlineSvg(schemeOutline)
        } else if (this._geodataDirty) {
          // Boundary was cleared — send empty string to remove on server
          gardenAttrs['outline-geometry'] = ''
        }

        await api.updateGarden(gardenId, properties, Object.keys(gardenAttrs).length > 0 ? gardenAttrs : undefined)

        const zonesPayload = properties.zones as { zones: unknown[]; pins: unknown[] }
        debug.log('SaveService', `Saved zone state: ${zonesPayload.zones.length} zones, ${zonesPayload.pins.length} pins`)
        if (anchor) debug.log('SaveService', 'Saved geodata: geoAnchor + parcel')
        if (gardenAttrs['outline-geometry']) debug.log('SaveService', 'Saved outline-geometry')
        this._zoneDirty = false
        this._geodataDirty = false
      } catch (error) {
        debug.warn('SaveService', 'Failed to save garden properties (objects saved OK)', error)
      }

      // Flush pending background image (deferred from new-garden WMS fetch)
      if (this._pendingBackgroundImage) {
        try {
          await this.saveBackgroundImage(
            gardenId,
            this._pendingBackgroundImage.dataUri,
            this._pendingBackgroundImage.x,
            this._pendingBackgroundImage.y,
            this._pendingBackgroundImage.scale,
            this._pendingBackgroundImage.rotation
          )
          debug.log('SaveService', 'Flushed pending background image')
        } catch (error) {
          debug.warn('SaveService', 'Failed to save pending background image', error)
        }
        this._pendingBackgroundImage = null
        this._backgroundDirty = false
      }

      // Capture preview BEFORE markAllClean() — markAllClean fires a 'bulk' change
      // that triggers async re-render (clear + reload) of ground/plant/building meshes.
      // If captured after, the scene would be empty (meshes cleared, async reload pending).
      if (this.onCapturePreview && gardenId) {
        try {
          const dataUri = this.onCapturePreview()
          if (dataUri) {
            await api.updateGarden(gardenId, {}, { 'preview-data-uri': dataUri })
            debug.log('SaveService', 'Preview thumbnail uploaded')
          }
        } catch (error) {
          debug.warn('SaveService', 'Failed to upload preview thumbnail (save OK)', error)
        }
      }

      // Mark all as clean after successful save
      gardenObjectState.markAllClean()

      this.setStatus('success')
      const created = dirtyObjects.filter(d => d.status === 'new').length
      const updated = dirtyObjects.filter(d => d.status === 'modified').length
      const deleted = dirtyObjects.filter(d => d.status === 'deleted').length
      debug.success('SaveService', `Save complete: ${created} created, ${updated} updated, ${deleted} deleted`)

      // Reset status after a delay
      setTimeout(() => {
        if (this._status === 'success') {
          this.setStatus('idle')
        }
      }, 2000)

      return true
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      debug.error('SaveService', 'Save failed', error)
      this.setStatus('error', msg)
      return false
    }
  }

  /**
   * Save a background image to the garden (WMS cadastral map)
   */
  async saveBackgroundImage(
    gardenId: string,
    dataUri: string,
    x: number,
    y: number,
    scale: number,
    rotation?: number
  ): Promise<void> {
    debug.log('SaveService', `Saving background image (${(dataUri.length / 1024).toFixed(0)}KB)`)

    const gardenAttrs: Record<string, unknown> = {
      'background-image-data-uri': dataUri,
      'background-offset-x': Math.round(x),
      'background-offset-y': Math.round(y),
      'background-scale': scale,
    }
    if (rotation !== undefined) {
      gardenAttrs['background-rotation'] = rotation
    }

    await api.updateGarden(gardenId, {}, gardenAttrs)
    debug.success('SaveService', 'Background image saved')
  }

  /**
   * Clear the background image on the server
   */
  async clearBackgroundImage(gardenId: string): Promise<void> {
    debug.log('SaveService', 'Clearing background image')

    await api.updateGarden(gardenId, {}, {
      'background-image-data-uri': '',
      'background-offset-x': 0,
      'background-offset-y': 0,
      'background-scale': 1,
      'background-rotation': 0,
    })
    debug.success('SaveService', 'Background image cleared')
  }

  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this._isNewGarden || gardenObjectState.isDirty() || this._zoneDirty || this._geodataDirty || this._backgroundDirty
  }

  /**
   * Mark that this is a new garden not yet created on server.
   * Makes hasUnsavedChanges() return true so the save dot shows.
   */
  markNewGarden(): void {
    this._isNewGarden = true
  }

  /**
   * Mark geodata as dirty (boundary import/edit)
   */
  markGeodataDirty(): void {
    this._geodataDirty = true
  }

  /**
   * Store a pending background image to be saved on next explicit save.
   * Used when garden has no server ID yet (new garden).
   */
  markBackgroundDirty(dataUri: string, x: number, y: number, scale: number, rotation?: number): void {
    this._pendingBackgroundImage = { dataUri, x, y, scale, rotation }
    this._backgroundDirty = true
  }

  /**
   * Clear any pending (not-yet-saved) background image.
   */
  clearPendingBackground(): void {
    this._pendingBackgroundImage = null
    this._backgroundDirty = false
  }

  /**
   * Suppress zone dirty tracking (call before importing saved zone state)
   */
  suppressZoneDirty(): void {
    this._zoneDirtyEnabled = false
  }

  /**
   * Reset zone dirty flag and re-enable tracking (call after import)
   */
  markZonesClean(): void {
    this._zoneDirty = false
    this._zoneDirtyEnabled = true
  }

  /**
   * Subscribe to status changes
   */
  onStatusChange(callback: SaveStatusCallback): () => void {
    this.statusCallbacks.push(callback)
    return () => {
      const index = this.statusCallbacks.indexOf(callback)
      if (index >= 0) this.statusCallbacks.splice(index, 1)
    }
  }

  private setStatus(status: SaveStatus, message?: string): void {
    this._status = status
    for (const cb of this.statusCallbacks) {
      try {
        cb(status, message)
      } catch (error) {
        debug.error('SaveService', 'Error in status callback', error)
      }
    }
  }
}

// Singleton
export const saveService = new SaveServiceManager()
